import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { officeLocationsAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Card, CardContent } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import { toast } from "sonner";
import {
  MapPin,
  Plus,
  Search,
  Edit,
  Power,
  PowerOff,
  ArrowLeft,
} from "lucide-react";
import { formatDateTime } from "../../../shared/lib/utils";

export default function OfficeLocationsManagerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form state
  const [officeName, setOfficeName] = useState("");
  const [officeCode, setOfficeCode] = useState("");
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    loadOffices();
  }, [statusFilter]);

  const loadOffices = async () => {
    try {
      const params = { include_inactive: true };
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      const res = await officeLocationsAPI.getAll(params);
      setOffices(res.data);
    } catch (err) {
      toast.error("Failed to load office locations");
    } finally {
      setLoading(false);
    }
  };

  const filteredOffices = offices.filter((o) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      o.office_name.toLowerCase().includes(query) ||
      (o.office_code && o.office_code.toLowerCase().includes(query)) ||
      (o.address && o.address.toLowerCase().includes(query))
    );
  });

  const resetForm = () => {
    setOfficeName("");
    setOfficeCode("");
    setAddress("");
    setEditingOffice(null);
  };

  const openEditDialog = (office) => {
    setEditingOffice(office);
    setOfficeName(office.office_name);
    setOfficeCode(office.office_code || "");
    setAddress(office.address || "");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!officeName) {
      toast.error("Office name is required");
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        office_name: officeName,
        office_code: officeCode || null,
        address: address || null,
      };

      if (editingOffice) {
        await officeLocationsAPI.update(editingOffice.id, data);
        toast.success("Office location updated");
      } else {
        await officeLocationsAPI.create(data);
        toast.success("Office location created");
      }

      setDialogOpen(false);
      resetForm();
      loadOffices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save office location");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (office) => {
    try {
      if (office.status === "active") {
        await officeLocationsAPI.deactivate(office.id);
        toast.success("Office location deactivated");
      } else {
        await officeLocationsAPI.reactivate(office.id);
        toast.success("Office location reactivated");
      }
      loadOffices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="office-locations-manager-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="font-mono text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading font-black text-2xl flex items-center gap-2">
            <MapPin className="h-6 w-6 text-[#FF6B00]" />
            Office Locations Manager
          </h1>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              className="h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider"
              data-testid="add-office-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Office
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-2 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingOffice ? "Edit Office Location" : "Add Office Location"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                  Office Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={officeName}
                  onChange={(e) => setOfficeName(e.target.value)}
                  placeholder="Fisk Operations Center"
                  className="h-12 rounded-none border-2 font-mono"
                  data-testid="office-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                  Office Code
                </Label>
                <Input
                  value={officeCode}
                  onChange={(e) => setOfficeCode(e.target.value)}
                  placeholder="FISK"
                  className="h-12 rounded-none border-2 font-mono"
                  data-testid="office-code-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                  Address
                </Label>
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="1100 Fisk Street"
                  className="h-12 rounded-none border-2 font-mono"
                  data-testid="office-address-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                data-testid="submit-office-btn"
              >
                {submitting ? <span className="spinner" /> : editingOffice ? "Update Office" : "Add Office"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-2 border-border rounded-none mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search offices..."
                  className="h-10 rounded-none border-2 font-mono pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Status
              </Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 rounded-none border-2 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <div className="font-mono text-sm text-muted-foreground">
                {filteredOffices.length} office(s)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Offices List */}
      <div className="space-y-3">
        {filteredOffices.map((office) => (
          <Card
            key={office.id}
            className={`border-2 rounded-none ${
              office.status === "inactive" ? "border-slate-300 bg-slate-50 dark:bg-slate-900/50" : "border-border"
            }`}
            data-testid={`office-item-${office.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-bold text-lg">
                      {office.office_name}
                    </span>
                    {office.office_code && (
                      <Badge variant="outline" className="rounded-none font-mono text-xs">
                        {office.office_code}
                      </Badge>
                    )}
                    <Badge
                      className={`rounded-none font-mono text-xs ${
                        office.status === "active"
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-500 text-white"
                      }`}
                    >
                      {office.status.toUpperCase()}
                    </Badge>
                  </div>
                  {office.address && (
                    <div className="text-sm text-muted-foreground">
                      {office.address}
                    </div>
                  )}
                  <div className="mt-2 font-mono text-xs text-muted-foreground">
                    Created: {formatDateTime(office.created_at)}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(office)}
                    className="rounded-none border-2 font-mono text-xs"
                    data-testid={`edit-office-${office.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(office)}
                    className={`rounded-none border-2 font-mono text-xs ${
                      office.status === "active"
                        ? "text-amber-600 border-amber-500"
                        : "text-emerald-600 border-emerald-500"
                    }`}
                    data-testid={`toggle-office-${office.id}`}
                  >
                    {office.status === "active" ? (
                      <>
                        <PowerOff className="h-3 w-3 mr-1" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <Power className="h-3 w-3 mr-1" />
                        Reactivate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
